/**
 * Launcher V2 A2 U5 — Gong flow picker: URL construction, response parsing,
 * and local state transitions for `GongFlowPicker.tsx` (plan R5, Key
 * Technical Decision 8: the picker calls core's real `/api/flow/gong/*`
 * endpoints directly over HTTP via `apiFetch` — no launcher-scoped proxy;
 * `src/api/flow-gong.ts:161-165`).
 *
 * ── PR B follow-up U8 (R8): pin/hide is REMOVED from this module —
 * `putGongFlowPreference`/`gongFlowPreferenceUrl`/
 * `applyGongFlowPreferenceLocally` are gone. This is launcher-side only: the
 * real preference feature (`PUT /api/flow/gong/flows/:flowId/preference`,
 * `src/api/flow-gong.ts`, the `gongFlowUserPref` table, Flow's own
 * `EnrollQueue` UI) is untouched — see the plan's hard scope fence. U9 adds
 * `groupGongFlowsByFolder`, a pure helper for the browse list's folder
 * sections.
 *
 * ── Live-QA round 2, Batch 4 (C1) — `toggleGongFlowSelection` is REMOVED:
 * `GongFlowPicker.tsx`'s row behavior is now single-click-assign (no
 * select-then-confirm toggle state), matching Flow's own `PlayPicker`/
 * `GongFlowBrowse` one-click pattern. `gongStepsToSequenceSteps` (the wire →
 * `SequenceStep` import mapper) and `importGongFlowSteps` (the pick → real
 * steps → saved copy orchestration) are new — see their own doc comments.
 *
 * Factored out of the component so it is unit-testable without a DOM —
 * `mini-apps/launcher` has no jsdom/happy-dom harness (see
 * `__tests__/v2-push-drawer.test.tsx`'s head-of-file caveat, and
 * `__tests__/v2-play-flow-shell.test.tsx`'s identical note about
 * `QualifyStep.tsx` transitively importing `@clay-run/glaze/dropdown`,
 * whose module top-level crashes bun:test's DOM-less runtime). The test file
 * for this module (`__tests__/v2-gong-flow-picker.test.tsx`) imports ONLY
 * this file, never `GongFlowPicker.tsx`.
 *
 * Wire schemas are the real core ones, reached through the `_shared`
 * re-export (`mini-apps/_shared/flow/gong.ts`) — mini-apps never import
 * `../../src` directly (`mini-apps/CLAUDE.md`), and these are NEVER
 * redeclared (`terra/no-parallel-schema-and-type`).
 */

import { z } from 'zod';

import {
  GongFlowBrowseResponseSchema,
  type GongFlowBrowseResponse,
  GongFlowSuggestedResponseSchema,
  type GongFlowSuggestedResponse,
  GongFlowStepsApiResponseSchema,
  type GongFlowStepsApiResponse,
  GongFlowStepDetailWireSchema,
  type GongFlowStepDetailWire,
  type GongFlowSummary,
} from '../_shared/flow/gong';
import { getSequence, type SequenceSettingsWriteOptions } from './api-client';
import { isArchivedInLibrary } from './play-ownership';
import { normalizeGongMergeTokens } from './copy-generation-rules';
import { containsRealHtmlTags } from './html-format';
import { createSequence, saveSequenceSteps, setSequenceFlow } from './mutations';
import { GONG_EMAIL_STEP_TYPES, GONG_STEP_TYPE_LABELS } from './step-content-rules';
import type {
  PlaybooksSaveSequenceStepsResponse,
  PlaybooksSequenceSummary,
  SequenceStep,
  SequenceSteps,
} from './schemas';

type ApiFetch = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

// ── URL construction ─────────────────────────────────────────────────────────
//
// `force=true` is the ONE query param either GET route reads (round-5 plan
// Decision #1 dropped browse's `folder`/`search`/`sort`/`cursor` — filtering
// happens client-side over the full catalog via `filterAndSortGongFlows`,
// re-exported unchanged from `_shared/flow/gong.ts`). Anything other than the
// literal string `'true'` means "don't force" server-side, so these builders
// never emit the param at all when `force` isn't explicitly `true` — no
// `?force=false` noise on the wire.

/** GET /api/flow/gong/flows — the full catalog; `force: true` bypasses the 15-min TTL. */
export function gongFlowsBrowseUrl(opts?: { force?: boolean }): string {
  return opts?.force ? '/api/flow/gong/flows?force=true' : '/api/flow/gong/flows';
}

/** GET /api/flow/gong/flows/suggested — no query params (Tier-1 ranking, up to 3 entries). */
export function gongFlowsSuggestedUrl(): string {
  return '/api/flow/gong/flows/suggested';
}

/** GET /api/flow/gong/flows/:flowId/steps — `force: true` bypasses the no-TTL cache's hit short-circuit. */
export function gongFlowStepsUrl(flowId: string, opts?: { force?: boolean }): string {
  const base = `/api/flow/gong/flows/${encodeURIComponent(flowId)}/steps`;
  return opts?.force ? `${base}?force=true` : base;
}

// ── Fetch wrappers (apiFetch injected — same throwing contract as api-client.ts) ──
//
// Every wrapper parses its response body through the matching zod schema —
// a malformed payload fails loudly at the schema-parse boundary rather than
// flowing an untyped shape into a consumer (same rule as `api-client.ts`).

export async function fetchGongFlowsBrowse(
  apiFetch: ApiFetch,
  opts?: { force?: boolean },
): Promise<GongFlowBrowseResponse> {
  const res = await apiFetch(gongFlowsBrowseUrl(opts));
  return GongFlowBrowseResponseSchema.parse(await res.json());
}

export async function fetchGongFlowsSuggested(apiFetch: ApiFetch): Promise<GongFlowSuggestedResponse> {
  const res = await apiFetch(gongFlowsSuggestedUrl());
  return GongFlowSuggestedResponseSchema.parse(await res.json());
}

export async function fetchGongFlowSteps(
  apiFetch: ApiFetch,
  flowId: string,
  opts?: { force?: boolean },
): Promise<GongFlowStepsApiResponse> {
  const res = await apiFetch(gongFlowStepsUrl(flowId, opts));
  return GongFlowStepsApiResponseSchema.parse(await res.json());
}

// ── Step import (Live-QA round 2, Batch 4, C1) ──────────────────────────────

/**
 * `GONG_STEP_TYPE_LABELS` / `gongStepTypeLabel` MOVED to
 * `step-content-rules.ts` (2026-09-24) and are re-exported here so every
 * existing importer keeps working. Why they moved: this file imports
 * `./mutations` → `./api-client`, and `dispatch-inspector-logic.ts` needs the
 * label — importing it from here dragged the whole fetch layer into the
 * Dispatch Inspector's module graph and broke
 * `__tests__/dispatch-inspector-clay-render.test.tsx`'s `mock.module`
 * (`SyntaxError: Export named 'archiveIdentityRequest' not found`).
 * `step-content-rules.ts` is the DOM-free, dependency-free leaf both sides can
 * read. See that file for the full doc comments.
 */
export { GONG_STEP_TYPE_LABELS, gongStepTypeLabel } from './step-content-rules';

// The email-family set this file used to keep privately as
// `GONG_HTML_BODY_STEP_TYPES` now lives in `step-content-rules.ts` as
// `GONG_EMAIL_STEP_TYPES` and is imported above — one email-family set in the
// app, not two. `SEND_EMAIL`/`AUTO_SEND_EMAIL` bodies are ALWAYS real HTML, of
// many possible shapes (Gong's authoring tool, a rep's manual edit, a prior
// import), not just the one per-line `data-default-styles` envelope
// `isGongHtmlEnvelope` sniffs for below — which is why `gongStepBodyFormat`
// treats the family unconditionally as `'rich'`; see that function's own doc
// for why a pure content-sniff covering ALL step types regressed it.

// Round-3-adversarial-review fix (Bug 3) — HTML5's actual whitespace
// definition is space, tab, LF, FF, CR ONLY. Vertical tab (`\v`, U+000B) is
// NOT HTML whitespace: a real parser treats it as a literal character inside
// an unquoted attribute value, so `title=x\vdata-default-styles` is ONE
// unquoted value (`x\vdata-default-styles`), never two separate tokens. The
// previous set wrongly included `\v`, which let a vertical tab masquerade as
// the separator between an unquoted value and a following bare attribute —
// confirmed: `<div title=x` + VT + `data-default-styles>Hi</div>` wrongly
// returned `true` (read as two attributes) before this fix.
const ASCII_WHITESPACE_CHARS = new Set([' ', '\t', '\n', '\r', '\f']);

/** A real HTML tag-name boundary: whitespace, `/`, `>`, or end of input — `<divx` is not a `<div` tag. */
function isTagBoundaryChar(ch: string | undefined): boolean {
  return ch === undefined || ch === '/' || ch === '>' || ASCII_WHITESPACE_CHARS.has(ch);
}

/**
 * Quote-aware scan from `start` forward to a tag's terminating, unquoted `>`
 * (a `>` inside a `"…"`/`'…'` attribute value is literal, not the terminator).
 * Returns the index just past that `>`, or `-1` if the tag never closes
 * before the end of the string (an unterminated tag).
 */
function findTagEnd(s: string, start: number): number {
  let inQuote: string | null = null;
  for (let idx = start; idx < s.length; idx++) {
    const ch = s[idx];
    if (inQuote) {
      if (ch === inQuote) inQuote = null;
      continue;
    }
    if (ch === '"' || ch === "'") {
      inQuote = ch;
      continue;
    }
    if (ch === '>') return idx + 1;
  }
  return -1;
}

/**
 * Quote-aware scan of ANY opening tag's attributes, starting right after the
 * tag's NAME (at `tagBoundaryIdx` — e.g. right after `<div`, `<span`, or
 * `<script`; this function has no opinion about which tag it was called for,
 * see {@link scanTag}, its one caller). Tokenizes attribute NAMES — a name
 * ends at `=`, whitespace, `/`, or `>` — and records whether any name is
 * exactly `data-default-styles`, covering both the bare form
 * (`<div data-default-styles>`) and the valued form
 * (`<div data-default-styles="…">`). Checking the NAME (not a substring
 * search over the whole tag) is what makes `title="data-default-styles"`
 * correctly fail: once `=` is seen the scanner switches to "reading a value"
 * mode, and a value's contents are only ever consumed looking for where the
 * value ends, never tokenized as a name.
 *
 * Three sub-states after a name, mirroring real HTML5 tokenizer semantics
 * (Codex finding 1 fix — the previous two-state version conflated "just saw
 * `=`" with "mid unquoted value", which is what produced its false
 * positives/negatives):
 *
 *  - `'name'` — scanning an attribute name (or the gap between attributes).
 *  - `'beforeValue'` — right after `=`, no value character seen yet. Leading
 *    whitespace here is part of "before attribute value" and does NOT end
 *    anything (`title= data-default-styles` must not read the space as
 *    ending an empty value and restart name-scanning — that was the false
 *    positive that let `data-default-styles` get re-tokenized as its own
 *    attribute name). A quote here starts a quoted value; anything else
 *    (including `/`) starts an unquoted value; `>` here is an empty value
 *    that immediately closes the tag.
 *  - `'unquotedValue'` — only whitespace or `>` end an unquoted value. `/` is
 *    a literal character inside it (real HTML5 semantics — the self-closing
 *    `/>` form only applies in the NAME/before-attribute-name state, never
 *    mid-value), which is what makes `title=x/data-default-styles` correctly
 *    keep `data-default-styles` inside `title`'s value instead of splitting
 *    it into a new bare attribute at the `/`.
 *
 * A quote that never closes falls out of the loop still `inQuote`, which
 * returns `end: -1` (genuinely malformed/unterminated) rather than silently
 * treating the quote character as an ordinary name character.
 *
 * Returns whether the marker was found and the index just past the tag's
 * terminating `>` (`-1` if the tag is unterminated). Named generically
 * (round-3-adversarial-review fix, Bug 1) because {@link scanTag} calls it for
 * every opening tag it tokenizes, not just `<div` — see that function's own
 * doc for why a generic "scan any tag's attributes, quote-aware" helper (this
 * one) shared between the div-specific depth walk and the raw-text/comment
 * detection is what closes the false-negative this bug fixes.
 */
function scanTagAttributes(s: string, tagBoundaryIdx: number): { foundMarker: boolean; end: number } {
  let idx = tagBoundaryIdx;
  let state: 'name' | 'beforeValue' | 'unquotedValue' = 'name';
  let tokenStart: number | null = null;
  let inQuote: string | null = null;
  let foundMarker = false;

  const flushName = (end: number): void => {
    if (tokenStart !== null && s.slice(tokenStart, end) === 'data-default-styles') {
      foundMarker = true;
    }
    tokenStart = null;
  };

  while (idx < s.length) {
    const ch = s[idx]!;
    if (inQuote) {
      if (ch === inQuote) {
        inQuote = null;
        state = 'name';
      }
      idx++;
      continue;
    }
    if (state === 'name') {
      if (ch === '=') {
        flushName(idx);
        state = 'beforeValue';
        idx++;
        continue;
      }
      if (ch === '>') {
        flushName(idx);
        return { foundMarker, end: idx + 1 };
      }
      if (ch === '/' || ASCII_WHITESPACE_CHARS.has(ch)) {
        flushName(idx);
        idx++;
        continue;
      }
      if (tokenStart === null) tokenStart = idx;
      idx++;
      continue;
    }
    if (state === 'beforeValue') {
      if (ASCII_WHITESPACE_CHARS.has(ch)) {
        idx++;
        continue;
      }
      if (ch === '"' || ch === "'") {
        inQuote = ch;
        idx++;
        continue;
      }
      if (ch === '>') {
        // Empty unquoted value, tag closes immediately.
        return { foundMarker, end: idx + 1 };
      }
      // Any other character — including `/` — starts an unquoted value.
      state = 'unquotedValue';
      idx++;
      continue;
    }
    // state === 'unquotedValue' — never tokenized as a name; `/` is literal.
    if (ch === '>') {
      return { foundMarker, end: idx + 1 };
    }
    if (ASCII_WHITESPACE_CHARS.has(ch)) {
      state = 'name';
      idx++;
      continue;
    }
    idx++;
  }
  return { foundMarker, end: -1 };
}

/** A tag name char — letters, digits, or `-` (custom-element names). */
function isTagNameChar(ch: string | undefined): boolean {
  return ch !== undefined && /[a-z0-9-]/.test(ch);
}

/** `<script`/`<style` — the two raw-text elements whose bodies {@link scanTag}'s caller must treat as opaque. */
const RAW_TEXT_TAG_NAMES: ReadonlySet<string> = new Set(['script', 'style']);

/**
 * Round-3-adversarial-review fix (Bug 1) — the SINGLE tokenizer every walk in
 * this module goes through to find out what starts at index `k`: a real
 * comment opener, a real closing tag, a real opening tag (with its own name
 * and attributes fully consumed, quote-aware), or nothing special at all.
 *
 * The bug this replaces: the previous depth walk (in {@link isGongHtmlEnvelope})
 * only recognized `<div`/`</div`/`<!--`/`<script`/`<style` by literal prefix
 * match at EVERY character position, falling back to a bare `k++` for
 * anything else — so it never actually tokenized whatever OTHER tag it was
 * looking at (e.g. `<span title="...">`). A literal substring that merely
 * LOOKED like one of those five prefixes but lived inside a completely
 * unrelated tag's quoted attribute VALUE — `<span title="<script>">` or
 * `<span title="<!--">` — got misread as the real thing, because the walk was
 * never inside a quote-aware scan of the span tag to begin with; it just
 * happened to reach that character position via plain `k++`. Confirmed:
 * `<div data-default-styles><span title="<script>">Hi</span></div>` and the
 * `<!--` equivalent both wrongly returned `false` (the walk went looking for a
 * `</script>`/comment-close that doesn't really exist as markup and never
 * found the real, already-present closing tags).
 *
 * The fix: NEVER inspect a character position without first asking "is this
 * the start of a real tag?", and if so, tokenize that WHOLE tag (name +
 * attributes, quote-aware, via {@link scanTagAttributes} — the exact same
 * quote-aware attribute scanner `<div`'s own marker check already used, now
 * shared and generalized rather than forked) before deciding anything about
 * it. A quoted attribute value's contents are consumed as part of that one
 * atomic tag scan and never re-examined as a fresh scan position — which is
 * what makes `<script>`/`<!--`-looking text trapped inside `<span title="…">`
 * inert: the walk never lands mid-attribute-value at all, because the whole
 * `<span ...>` tag was already consumed as one unit by the time the walk's
 * cursor moves past it.
 *
 * Returns:
 *  - `{ kind: 'not-tag' }` — `k` is not `<`, or `<` is not followed by a real
 *    tag-name-start/`/`/`!--` (e.g. a bare `<` in body text, or `< div>` with a
 *    space) — caller advances by one character, same as before.
 *  - `{ kind: 'comment', end }` — `<!-- … -->`; `end === -1` if unterminated.
 *  - `{ kind: 'open', name, foundMarker, end }` — an opening tag, fully
 *    consumed; `end === -1` if it never closes. `name` is whatever real,
 *    lowercase tag name was tokenized (`div`, `script`, `span`, …) — the
 *    caller decides what (if anything) that name means.
 *  - `{ kind: 'close', name, end }` — a closing tag, fully consumed the same
 *    way (closing tags don't carry a marker, only a name).
 *
 * Linear time: `k` only ever moves forward, here and in every caller — no
 * position is ever re-scanned once consumed as part of a tag.
 */
type TagScanResult =
  | { kind: 'not-tag' }
  | { kind: 'comment'; end: number }
  | { kind: 'open'; name: string; foundMarker: boolean; end: number }
  | { kind: 'close'; name: string; end: number };

function scanTag(s: string, k: number): TagScanResult {
  if (s[k] !== '<') return { kind: 'not-tag' };
  if (s.slice(k, k + 4) === '<!--') {
    const closeIdx = s.indexOf('-->', k + 4);
    return { kind: 'comment', end: closeIdx === -1 ? -1 : closeIdx + 3 };
  }
  const isClosing = s[k + 1] === '/';
  const nameStart = isClosing ? k + 2 : k + 1;
  if (!isTagNameChar(s[nameStart])) return { kind: 'not-tag' };
  // Round-4-adversarial-review fix (Bug 3) — a real HTML tokenizer's tag-name
  // state consumes EVERY character up to the next genuine tag boundary
  // (whitespace, `/`, `>`, or end of input — real "attribute name" characters
  // never end a tag name early), so `<div.foo`/`<div_foo`/`<div:foo` tokenize
  // as the tag names `div.foo`/`div_foo`/`div:foo`, never as `div` with `.foo`
  // /`_foo`/`:foo` folded into attribute scanning. The previous loop stopped
  // consuming at the first character outside `isTagNameChar`'s narrower
  // `[a-z0-9-]` set (the legal *start* charset for a real element/custom-
  // element name), which let all three inputs mis-tokenize as a genuine `div`
  // open with `.foo`/`_foo`/`:foo data-default-styles` absorbed into its
  // attribute scan — confirmed empirically these wrongly returned `true`
  // before this fix, both for `isGongHtmlEnvelope`'s open-`<div` check and for
  // the closing `</div` path below (same `nameEnd` computation feeds both),
  // so `</div.foo>` is fixed identically by this one change.
  let nameEnd = nameStart + 1;
  while (!isTagBoundaryChar(s[nameEnd])) nameEnd++;
  const name = s.slice(nameStart, nameEnd);
  if (isClosing) {
    return { kind: 'close', name, end: findTagEnd(s, nameEnd) };
  }
  const { foundMarker, end } = scanTagAttributes(s, nameEnd);
  return { kind: 'open', name, foundMarker, end };
}

/**
 * Given an already-tokenized `<script`/`<style` OPEN tag (name in
 * {@link RAW_TEXT_TAG_NAMES}) whose own opening `>` ends at `openTagEnd`,
 * finds that element's literal closing tag and returns the index just past
 * it — a plain string search, never another {@link scanTag} call, because a
 * real HTML parser never scans a raw-text element's body for markup at all
 * (a `</div>`-looking substring inside a `<script>` string literal is not
 * markup). Returns `-1` if the closing tag never appears, or the opening tag
 * itself never closed (`openTagEnd === -1`, checked by the caller).
 *
 * Linear time: the forward scan for the closing tag never revisits a
 * character the outer depth walk would otherwise have looked at.
 */
function skipRawTextBody(s: string, name: string, openTagEnd: number): number {
  if (openTagEnd === -1) return -1;
  const closeTag = `</${name}`;
  for (let scan = openTagEnd; scan < s.length; scan++) {
    if (s.slice(scan, scan + closeTag.length) === closeTag && isTagBoundaryChar(s[scan + closeTag.length])) {
      return findTagEnd(s, scan + closeTag.length);
    }
  }
  return -1;
}

/**
 * Gong's real per-step HTML envelope (`src/shared/schemas/gong-flow.ts:27-31`'s
 * doc comment, confirmed live per `docs/flow/gong-flow-play-spec-handoff.md`
 * Check 3: "body = HTML with Gong's `data-default-styles` div wrapper" — for
 * EVERY step type, not just email). Gong's own authoring tool emits one such
 * div PER VISUAL LINE for a multi-line body (a new `<div data-default-styles
 * ...>` per line, rather than one wrapping div with internal `<br>`s), so the
 * envelope can legitimately be more than one of these blocks concatenated.
 *
 * Plan `2026-09-03-002-launcher-v2-round2-followup-codex-fixes-plan.md`, Unit
 * B1 / decision 7 — this used to be a single regex
 * (`GONG_HTML_ENVELOPE_RE`), replaced here by a single-pass linear scanner
 * (no regex, no DOM — this module's tests run under `bun:test` with no DOM
 * environment registered, so a `DOMParser`-based check would be untestable
 * where its tests live). The regex had three confirmed bugs this scanner
 * fixes: (1) catastrophic backtracking — a malformed/adversarial body could
 * take exponential time and freeze the render thread; (2) a false POSITIVE
 * when the marker string appeared only inside an unrelated attribute VALUE
 * (`title="data-default-styles"`) or when the lazy wildcard absorbed across
 * block boundaries (`<div data-default-styles>Hi</div><div>No marker</div>`);
 * (3) a false NEGATIVE when an envelope block legitimately contained an inner
 * `<div>` — the lazy match terminated at the inner `</div>` and then failed.
 *
 * Single pass over the string, case-insensitive (ASCII), tracking one block
 * at a time:
 *
 *  1. Skip whitespace. End of input: `true` only if at least one block was
 *     consumed (empty/whitespace-only input is `false`).
 *  2. Tokenize whatever starts here via {@link scanTag} and require it to be a
 *     real opening `div` tag carrying an exact `data-default-styles`
 *     attribute name. Not found (or the tag never closes): the whole body is
 *     `false` — every malformed/ambiguous shape degrades to `false`
 *     (`'plain'`), the safe direction (a lossless plain textarea vs. the rich
 *     editor's lossy sanitizer).
 *  3. Walk forward tracking div-nesting depth, tokenizing EVERY position via
 *     {@link scanTag} before deciding anything about it (round-3-adversarial-
 *     review fix, Bug 1 — see that function's own doc for the false-negative
 *     this replaces): a real opening/closing `div` adjusts depth by ±1; a
 *     `<script>`/`<style>` opening tag skips its whole raw-text body as one
 *     opaque unit ({@link skipRawTextBody}) so a `</div>`-looking string
 *     literal inside it is never read as markup; a real `<!-- … -->` comment
 *     is skipped whole for the same reason; ANY OTHER real tag (`<span>`,
 *     `<a href="…">`, …) is tokenized and skipped as one atomic unit with NO
 *     depth change — which is what makes a `<script>`/`<!--`-looking
 *     substring trapped inside THAT tag's own quoted attribute value inert,
 *     since the whole tag (quotes included) is consumed before the walk's
 *     cursor can ever land inside it. Anything that isn't the start of a real
 *     tag at all (`not-tag`) is plain text, advanced one character at a time.
 *     Depth returning to zero ends this block; reaching the end of input with
 *     depth still open (unterminated) is `false`.
 *  4. Loop back to 1 for the next block.
 *
 * This is linear time by construction: every index the scan touches
 * (`scanTag`/`scanTagAttributes`/`findTagEnd`/`skipRawTextBody`'s internal
 * cursors, and the outer block loop's cursor) only ever moves forward — no
 * backtracking is possible.
 *
 * Used only for the NON-email promotion path in `gongStepBodyFormat` below —
 * email-family steps never reach this check, since they're already
 * unconditionally rich via `GONG_EMAIL_STEP_TYPES`.
 */
export function isGongHtmlEnvelope(body: string): boolean {
  const s = body.toLowerCase();
  const len = s.length;
  let i = 0;
  let consumedAnyBlock = false;

  while (true) {
    while (i < len && ASCII_WHITESPACE_CHARS.has(s[i]!)) i++;
    if (i >= len) return consumedAnyBlock;

    const openTag = scanTag(s, i);
    if (openTag.kind !== 'open' || openTag.name !== 'div' || openTag.end === -1 || !openTag.foundMarker) {
      return false;
    }

    let depth = 1;
    let k = openTag.end;
    while (depth > 0) {
      if (k >= len) return false;
      const tag = scanTag(s, k);
      if (tag.kind === 'not-tag') {
        k++;
        continue;
      }
      if (tag.kind === 'comment') {
        if (tag.end === -1) return false;
        k = tag.end;
        continue;
      }
      if (tag.kind === 'open') {
        if (tag.name === 'div') {
          if (tag.end === -1) return false;
          depth++;
          k = tag.end;
          continue;
        }
        if (RAW_TEXT_TAG_NAMES.has(tag.name)) {
          const rawEnd = skipRawTextBody(s, tag.name, tag.end);
          if (rawEnd === -1) return false;
          k = rawEnd;
          continue;
        }
        // Any other real tag (`<span>`, `<a href="…">`, …): consumed
        // atomically, no depth change — see this function's own doc.
        if (tag.end === -1) return false;
        k = tag.end;
        continue;
      }
      // tag.kind === 'close'
      if (tag.end === -1) return false;
      if (tag.name === 'div') depth--;
      k = tag.end;
    }
    i = k;
    consumedAnyBlock = true;
  }
}

/**
 * Plan `2026-09-03-001-launcher-v2-round2-followup-plan.md`, Unit A2 — a
 * HYBRID of step-type and body-content, not a replacement of one by the
 * other. This session's first pass at A2 replaced the step-type check
 * entirely with a pure content sniff and broke a real, pre-existing case:
 * plenty of email-family (`SEND_EMAIL`/`AUTO_SEND_EMAIL`) bodies are
 * ordinary inline HTML (`Hi <strong>Jamie</strong>`) that never happens to be
 * wrapped in the one specific `data-default-styles` envelope
 * `GONG_HTML_ENVELOPE_RE` sniffs for — a pure sniff wrongly demoted those to
 * `'plain'`. The rule actually needed is two-tiered:
 *
 *  1. Email-family steps (`GONG_EMAIL_STEP_TYPES`) are UNCONDITIONALLY
 *     `'rich'` — zero content-sniffing — exactly as the ORIGINAL
 *     (pre-this-session) step-type check treated them. Gong's wire format is
 *     real HTML for these two step types, full stop, regardless of shape.
 *  2. Every OTHER recognized step type (`CALL`/`LINKEDIN_CONNECT`/
 *     `LINKEDIN_MESSAGE`/`CUSTOM`) defaults to `'plain'` — Gong's wire shape
 *     doubles `body` as the call/LinkedIn SCRIPT for these, which is
 *     typically plain text and can carry a bracket-style placeholder token
 *     (e.g. `Call <Acme> then follow up`) that the rich contentEditable
 *     editor's HTML sanitizer would silently corrupt (Live-QA round 2 Codex
 *     finding 1) — UNLESS the body demonstrably matches Gong's real HTML
 *     envelope shape ({@link isGongHtmlEnvelope}) OR contains a real
 *     whitelisted HTML tag ({@link containsRealHtmlTags}, `html-format.ts`),
 *     in which case it's promoted to `'rich'`. The second check is
 *     round-4's Decision 5 (Spencer, 2026-09-13): the prior fix only covered
 *     Gong's one specific `data-default-styles` envelope shape, so a Call or
 *     LinkedIn step whose body came back as `<ul><li>…</li></ul>`, a bare
 *     `<br/>`, or any other real-but-unenveloped markup still showed literal
 *     tags in the plain textarea. HTML is never stripped or modified — a
 *     step body that contains real markup now always renders/edits rich and
 *     round-trips to Gong byte-for-byte unchanged; a bracket-style
 *     placeholder like `<Acme>` still stays plain, since it never matches
 *     either check.
 *
 * No descriptor at all, or an unrecognized/hostile `step_type` (the
 * descriptor is an opaque, foreign-controlled `z.record`, Key Decision 12):
 * degrades straight to `'plain'`, the same safe fallback the old step-type-only
 * check had — no content-sniff promotion is attempted for a step type this
 * function can't positively identify. `hasOwnProperty.call` (not the `in`
 * operator) guards this exactly like `gongStepTypeLabel` does, for the same
 * prototype-chain reason (Codex finding 3): `in` would let a hostile
 * `step_type` string naming an `Object.prototype` member (`'__proto__'`,
 * `'constructor'`, …) read back an INHERITED value instead of `undefined`.
 *
 * `body` is still read live, per call, from whatever is being rendered right
 * now — never cached — for the same reason as before: `CopyDiffView.tsx`
 * calls this once for a sequence's saved head body and once for a freshly
 * AI-generated candidate body for the SAME step position, DELIBERATELY
 * independently (see its own "computed independently on purpose" doc
 * comment), because the two bodies can be shaped completely differently even
 * though `copy-generation.ts` forwards the same `gongStepDescriptor`
 * (`step_type` included) onto the generated step unmodified — that's exactly
 * why `step_type` alone is safe to reuse across head/generated (it doesn't
 * change), but `body` never is.
 */
export function gongStepBodyFormat(
  body: string | undefined,
  descriptor: Record<string, unknown> | undefined,
): 'plain' | 'rich' {
  const stepType = descriptor?.step_type;
  if (
    typeof stepType !== 'string' ||
    !Object.prototype.hasOwnProperty.call(GONG_STEP_TYPE_LABELS, stepType)
  ) {
    return 'plain';
  }
  if (GONG_EMAIL_STEP_TYPES.has(stepType)) {
    return 'rich';
  }
  return typeof body === 'string' && (isGongHtmlEnvelope(body) || containsRealHtmlTags(body))
    ? 'rich'
    : 'plain';
}

/**
 * The subset of Gong's wire step this app snapshots onto a `SequenceStep`'s
 * `gongStepDescriptor` — `.pick()`ed off the real wire schema, never
 * redeclared (`terra/no-parallel-schema-and-type`, and the type is derived
 * with `z.infer`, not hand-written alongside it).
 *
 * Deliberately SMALL. The descriptor is an opaque, lossless, foreign-shaped
 * `z.record` where it is PERSISTED (`SequenceStepSchema.gongStepDescriptor`,
 * `schemas.ts`) and that stays true — ~every row stored before today lacks
 * `is_reply` entirely, so tightening the DB-JSON shape would turn every one of
 * them into a `db_json_schema_mismatch` and a null read. This type exists only
 * at the WRITE end, so that {@link gongStepsToSequenceSteps} cannot quietly
 * drop a field Gong sent: omit one and it is a compile error.
 *
 * `is_reply` is snapshotted for LOSSLESSNESS only. Nothing reads it to decide
 * whether a step is a reply — that is {@link isReplyAtOrdinal} against the
 * flow's live `replyStepOrders`, because this copy is the IMPORTED step's flag
 * and a reorder makes it wrong (see the reply-step contract note above).
 */
export const GongStepDescriptorSchema = GongFlowStepDetailWireSchema.pick({
  step_type: true,
  step_order: true,
  is_reply: true,
});
export type GongStepDescriptor = z.infer<typeof GongStepDescriptorSchema>;

// ── The reply-step contract: ONE rule, two readers ──────────────────────────
//
// Gong REJECTS an override that sets a subject on a REPLY step
// (`Step subject cannot be set for reply steps`) and 400s the ENTIRE
// enrollment over it — not just the offending step. 459 of 723 real flows
// have at least one reply step, so getting this wrong is not a corner case.
//
// The rule GONG honours is positional: the step it calls N is the Nth entry of
// the override array. `send-payload.ts#toOverrideSteps` therefore decides
// reply-ness by a step's ORDINAL in the position-sorted sequence, checked
// against the `gong_flows` mirror's `TargetFlowShape.replyStepOrders`
// (`stepOrder = overrideSteps.length + 1` over `[...steps].sort(by position)`).
// Since that is the rule the wire obeys, it is the only rule that can be
// right, and the editor must not have a second one.
//
// {@link sortStepsByPosition} + {@link isReplyAtOrdinal} ARE that rule, factored
// out so the editor's hosts can ask the same question before a rep types.
// `toOverrideSteps` keeps its own inline copy of the numbering (it is fused
// into the per-step resolution fold there), and the two are pinned to each
// other by doc comment in both directions — change one and change the other.
//
// WHAT THIS REPLACED, so it is not reintroduced: the editor used to call a
// `gongStepIsReply(step.gongStepDescriptor)` helper that read
// `descriptor.is_reply`. Two independent defects. (1) The descriptor's only
// producer ({@link gongStepsToSequenceSteps}) never wrote `is_reply`, so the
// answer was ALWAYS false — a Subject rendered on every reply step and the
// send then silently blanked whatever the rep typed (Devin D3). (2) Even with
// the producer fixed, the descriptor is the IMPORTED step's flag: reply-ness
// belongs to the flow's SLOT, not to the copy sitting in it, so the first
// reorder made it lie in both directions (Codex C4).

/**
 * The 1-based `step_order`s a Gong flow treats as replies, read off the real
 * wire steps (`GET /api/flow/gong/flows/:flowId/steps`).
 *
 * The same set `TargetFlowShape.replyStepOrders` carries on the send side —
 * that one is derived from the `gong_flows` MIRROR rows
 * (`toTargetFlowShape`, `send-payload.ts`), this one from the fetch response,
 * and that endpoint is mirror-first with a live fallback that upserts, so the
 * two agree by construction.
 *
 * An empty set is the honest answer for "this flow's shape is unknown" (no
 * flow bound, steps not loaded yet, a non-Gong channel): every step keeps its
 * Subject, exactly as `toOverrideSteps` changes nothing when `targetFlow` is
 * `undefined`. Never a guess in the hiding direction — hiding a field a rep
 * needs is the worse failure.
 */
export function replyStepOrdersOf(steps: readonly GongFlowStepDetailWire[]): ReadonlySet<number> {
  return new Set(steps.filter((step) => step.is_reply).map((step) => step.step_order));
}

/**
 * A sequence version's steps in the order Gong will number them.
 *
 * `SequenceStepsSchema` guarantees positions are UNIQUE, not that the stored
 * array is sorted (Key Decision 12 — it is a whole-snapshot array), which is
 * why `toOverrideSteps` sorts before numbering and why this exists rather than
 * each host sorting for itself. Byte-for-byte the same comparator as that
 * function's `[...steps].sort((a, b) => a.position - b.position)`.
 */
export function sortStepsByPosition(steps: readonly SequenceStep[]): SequenceStep[] {
  return [...steps].sort((a, b) => a.position - b.position);
}

/**
 * QA round 7, U5 (item 7) — THE step number a rep sees: the step's 1-based
 * index in the position-sorted list, which is exactly the `stepOrder`
 * `toOverrideSteps` hands Gong and the number `DispatchInspector` already
 * prints. Authoring surfaces used `position + 1` instead; the two agree only
 * while stored positions are contiguous from 0, and `send-payload.ts`'s own
 * comments record the bug when they were not. Falls back to `position + 1`
 * for a position not in `sortedSteps` (a draft row the head has not seen) so
 * a label is never blank.
 */
export function stepOrdinal(sortedSteps: readonly Pick<SequenceStep, 'position'>[], position: number): number {
  const index = sortedSteps.findIndex((step) => step.position === position);
  return index === -1 ? position + 1 : index + 1;
}

/**
 * Whether Gong will treat the step at `position` as a REPLY in the previous
 * step's thread — the editor's half of the one rule described above.
 *
 * `sortedSteps` must come from {@link sortStepsByPosition}; the step's ordinal
 * is its INDEX IN THAT LIST + 1, which is precisely the `stepOrder` value
 * `toOverrideSteps` computes for it (`overrideSteps.length + 1`, pushed in
 * sorted order). `replyOrders` comes from {@link replyStepOrdersOf} (in
 * practice via `use-gong-reply-step-orders.ts`).
 *
 * `false` is the answer for an unknown shape — an empty `replyOrders`, or a
 * `position` that is not in the list at all (a step mid-add, say). It reads
 * "show the Subject", matching the send's "shape unknown, change nothing"
 * rather than borrowing a neighbouring step's ordinal.
 */
export function isReplyAtOrdinal(
  sortedSteps: readonly SequenceStep[],
  position: number,
  replyOrders: ReadonlySet<number>,
): boolean {
  const index = sortedSteps.findIndex((step) => step.position === position);
  if (index === -1) return false;
  return replyOrders.has(index + 1);
}

/**
 * The import mapper (plan C1): a Gong flow's real steps, as fetched from
 * `GET /api/flow/gong/flows/:flowId/steps`, become this app's own
 * `SequenceStep`s. `position` is the ARRAY INDEX, not the wire's 1-based
 * `step_order` — `position` is this app's own zero-based ordering key
 * (`SequenceStepsSchema`'s uniqueness/ordering invariant), and Gong's
 * `step_order` is preserved losslessly inside `gongStepDescriptor` (the
 * opaque per-channel snapshot field, Key Decision 12) alongside `step_type`
 * and `is_reply` ({@link GongStepDescriptorSchema} is that write-end shape),
 * so nothing is lost even though nothing renders `step_order` directly today.
 *
 * `is_reply` is snapshotted for the record and NOTHING READS IT to decide
 * whether a step is a reply — the editor asks {@link isReplyAtOrdinal} against
 * the bound flow's LIVE reply-step orders instead, because this value is the
 * imported step's flag and a reorder makes it wrong. See the "reply-step
 * contract" note above.
 *
 * Non-email step types (CALL / LINKEDIN_CONNECT / LINKEDIN_MESSAGE / CUSTOM)
 * map identically at the STRUCTURAL level — Gong's own wire shape already
 * doubles `body` as the call/LinkedIn script for those
 * (`src/shared/schemas/gong-flow.ts`), so there is nothing to special-case in
 * this function. Whether that body renders/edits as rich HTML or plain text
 * is decided by the editor's host via {@link gongStepBodyFormat}, which reads
 * BOTH the step's own `gongStepDescriptor.step_type` (email-family is always
 * rich) AND its current `body` content (a non-email step is promoted to rich
 * only when its body matches Gong's real HTML envelope shape) — see that
 * function's own doc for the full hybrid rule. `gongStepDescriptor.step_type`
 * (via {@link gongStepTypeLabel}) is also what lets a step card show a
 * channel-appropriate chip.
 *
 * U8 (plan Key Technical Decision 9, R12) — `subject`/`body` are also run
 * through {@link normalizeGongMergeTokens} here, at the Gong FETCH boundary,
 * so an imported step carries Terra's `{{Token}}` merge-token vocabulary
 * rather than Gong's own `{{recipient.first_name}}`/`{{sender.first_name}}`/
 * `{{contact.<Field>__c}}` syntax. This is the ONLY normalization point —
 * `copy-generation.ts`'s AI generation/validation pass never remaps a token,
 * by design (see its remap guard). `gongStepDescriptor` (including the raw
 * `step_type`/`step_order` Gong sent) is left untouched — it is an opaque
 * snapshot field, not rendered copy.
 */
export function gongStepsToSequenceSteps(wireSteps: readonly GongFlowStepDetailWire[]): SequenceSteps {
  return wireSteps.map((step, index) => ({
    position: index,
    subject: normalizeGongMergeTokens(step.subject),
    body: normalizeGongMergeTokens(step.body),
    // Typed, not a loose literal: {@link GongStepDescriptorSchema} is the
    // write-end shape, so a field Gong sends and this mapper forgets is a
    // compile error rather than a key that silently never appears. `is_reply`
    // rides along for losslessness ONLY — see that schema's doc.
    gongStepDescriptor: {
      step_type: step.step_type,
      step_order: step.step_order,
      is_reply: step.is_reply,
    } satisfies GongStepDescriptor,
  }));
}

/**
 * The import ORCHESTRATION (plan C1's numbered sequence, steps 1-3 — step 4,
 * cache-patch + toast, is a UI-side concern the caller owns, same as every
 * other function in this DOM-less module):
 *
 *  1. Fetch the flow's real steps FIRST (`force: true`, bypassing the no-TTL
 *     cache's hit short-circuit) — if Gong can't produce steps, nothing below
 *     has bound or overwritten anything yet.
 *  2. Bind the flow (`setSequenceFlow`, `PATCH /v2/sequences/:id/flow`).
 *     ONLY the first-bind and rebind-SAME-flow cases are this function's
 *     job — both are idempotent/harmless on the server (`setSequenceFlow`'s
 *     own doc). A rebind onto a genuinely DIFFERENT already-bound flow throws
 *     `invalid_reference`; routing that case through a destructive-confirm
 *     diff instead is D4 (Batch 6, not yet built) — callers must not reach
 *     this function for that case.
 *  3. Save the mapped steps as the sequence's whole-snapshot copy
 *     (`saveSequenceSteps`), overwriting whatever placeholder steps existed —
 *     "load the gong steps in" is the point.
 *
 * No live caller exists yet (Batch 4 ships this function + the retry
 * affordance for an ALREADY-bound-but-empty sequence, `SequencesStep.tsx`'s
 * `handleLoadStepsFromGong`; the first-bind picker flow's caller is D2's Gong
 * tab, Batch 6) — same "no host yet" interim state as `GongFlowPickerInline`,
 * expected and documented, not a bug.
 *
 * Round-3-QA-fixes plan, Unit C2 / decision 10 — `confirmedMappingCount`, when
 * passed, is the shared-save acknowledgment forwarded as `saveSequenceSteps`'s
 * fifth argument: a sequence used in 2+ places MUST pass the count the caller
 * already displayed, or the server's shared-save recheck defaults to `1` and
 * refuses with a `shared_mapping_conflict` 409.
 *
 * Round-4 QA codex-review fix (wave 1, finding 1) — `confirmedUnsentPlayCount`
 * is the SECOND axis of that same acknowledgment, and omitting it was the
 * identical bug one level down: the server independently defaults
 * `confirmedUnsentPlayCount` to `1` (`api.ts`'s save endpoint) and 409s once
 * `countUnsentPlaysForSequence > 1`, so a sequence attached to 2+ DRAFT plays
 * failed every Gong import deterministically — and because this function
 * binds the flow BEFORE saving, that failure left the flow binding committed
 * with no steps behind it. Callers source it fresh from either
 * `distinctUnsentPlays(places)` (when they already fetch usage) or
 * `GET /v2/sequences/:id`'s `activeUnsentPlayCount` (when they already fetch
 * the detail) — never a stale cached value. It is forwarded as the SEVENTH
 * argument; the sixth (`generationInputs`) is deliberately `undefined` here,
 * since a Gong import carries no AI provenance.
 */
export async function importGongFlowSteps(
  apiFetch: ApiFetch,
  params: {
    sequenceId: string;
    flowId: string;
    headVersionId: string;
    confirmedMappingCount?: number;
    confirmedUnsentPlayCount?: number;
    /**
     * Plan 2026-09-25-002, U7 (Decision 21) — the ownership guard's query
     * param, forwarded to both writes below; TIGHTENED to a union by plan
     * 2026-09-29-001 U1 (R3/R5/R14) — an in-play import now names its group
     * too (the server 400s `group_required` on a body with `playId` and no
     * `rungId`). `undefined` in the playbook doorway.
     */
  } & SequenceSettingsWriteOptions,
): Promise<{ steps: SequenceSteps; save: PlaybooksSaveSequenceStepsResponse }> {
  const { sequenceId, flowId, headVersionId, confirmedMappingCount, confirmedUnsentPlayCount } = params;
  const stepsResponse = await fetchGongFlowSteps(apiFetch, flowId, { force: true });
  const steps = gongStepsToSequenceSteps(stepsResponse.steps);
  if (params.playId === undefined) {
    await setSequenceFlow(apiFetch, sequenceId, flowId);
    const save = await saveSequenceSteps(apiFetch, sequenceId, steps, headVersionId, confirmedMappingCount, undefined, confirmedUnsentPlayCount);
    return { steps, save };
  }
  const opts = { playId: params.playId, group: params.group };
  await setSequenceFlow(apiFetch, sequenceId, flowId, opts);
  const save = await saveSequenceSteps(
    apiFetch,
    sequenceId,
    steps,
    headVersionId,
    confirmedMappingCount,
    undefined,
    confirmedUnsentPlayCount,
    opts,
  );
  return { steps, save };
}

// ── Gong tab dispatch (Live-QA round 2, Batch 6, D2 + D4) ──────────────────

/**
 * The three-way (four, counting the same-flow no-op) outcome of a single
 * flow-row click in `SequenceMappingDrawer`'s Gong tab. `resolveGongFlowPick`
 * decides which applies AND performs every write that doesn't need a confirm
 * — only `'confirm-rebind'` leaves a write outstanding, because its write
 * would destroy the sequence's current saved copy and the caller must show
 * the danger-styled diff first (plan D4, mirroring the `confirmSwap`
 * fetch-before-open pattern: everything a confirm dialog shows must already
 * be resolved before it opens).
 */
export type GongFlowPickOutcome =
  /** Case 1 (rung unmapped, or mapped to a non-Gong sequence): a library
   *  sequence already bound to this flow was reused, or a brand-new one was
   *  created and imported. Either way the caller only needs to SELECT
   *  `sequenceId` in the drawer — the rung mapping itself still goes through
   *  the drawer's own Save, exactly like the existing "create sequence" flow. */
  | { kind: 'selected'; sequenceId: string; created: boolean }
  /** The picked flow is already what this mapped Gong sequence is bound to —
   *  nothing changed, nothing to write. */
  | { kind: 'unchanged'; sequenceId: string }
  /** Case 2 (mapped Gong sequence, first bind) or case 3 collapsed into case 2
   *  (a rebind whose current head has no steps to lose) — the import already
   *  happened; no confirm was needed. `save` is the real response so the
   *  caller can patch the `sequenceDetailQueryKey(id)` cache the same
   *  shape `SequencesStep.tsx`'s `applySequenceHeadToCache` already uses. */
  | {
      kind: 'imported';
      sequenceId: string;
      stepCount: number;
      flowName: string;
      save: PlaybooksSaveSequenceStepsResponse;
    }
  /** Case 3 (mapped Gong sequence, already flow-bound to a DIFFERENT flow,
   *  and its current head has steps that would be destroyed) — nothing has
   *  been written yet. `fromFlowId` is the id this sequence is bound to
   *  today; it can be `null` in the rare edge case where an unbound-but-
   *  ALREADY-non-empty sequence (contrary to the "placeholder/empty by
   *  construction" assumption of a first bind) needs the same confirm. */
  | {
      kind: 'confirm-rebind';
      sequenceId: string;
      fromFlowId: string | null;
      headSteps: SequenceSteps;
      headVersionId: string;
      mappedSteps: SequenceSteps;
      activeMappingCount: number;
      /** Round-4 QA codex-review fix (wave 1, finding 1) — the unsent-play
       *  axis of the same acknowledgment, resolved at the same moment as
       *  `activeMappingCount` so `commitGongFlowRebind` writes with the exact
       *  pair this confirm dialog's own copy was built from. */
      activeUnsentPlayCount: number;
    };

/**
 * Plan 2026-09-27-007 U3 — the counts-based twin of
 * `usage-gate.ts#inPlayUsageGate` (plan 2026-09-28-007 U7 — was
 * `usageGateNeedsConfirmation`, deleted), for the in-play Gong rebind
 * confirm: a sequence is sole-use when neither usage axis the confirm's own
 * `getSequence` resolved counts more than one place. Plan 2026-09-28-006 U6
 * (rulebook R5) — {@link resolveGongFlowPick} reads it to decide whether an
 * in-play first bind onto an empty sequence may import silently (sole-use)
 * or must go through the drawer's Save menu (shared). The drawer's own
 * rebind gate re-decides from FRESH usage places
 * (`usage-gate.ts#inPlayUsageGate`), never from these counts.
 */
export function gongRebindIsSoleUse(outcome: { activeMappingCount: number; activeUnsentPlayCount: number }): boolean {
  return outcome.activeMappingCount <= 1 && outcome.activeUnsentPlayCount <= 1;
}

/**
 * Cheap client-side reuse check (plan D2, case 1): a library sequence already
 * bound to this exact flow means there is nothing to create — picking the
 * same Gong flow twice from two different rungs should not spawn two
 * separate Sequences for it.
 *
 * R5-11 — exported: `SequenceMappingDrawer.tsx`'s Gong tab "Browse Gong
 * flows" doorway needs this SAME reuse check ahead of `resolveGongFlowPick`
 * (whose case-1 branch calls it internally) to decide, on a pick, whether to
 * commit straight to an existing sequence or open the create form — see that
 * file's module doc.
 */
export function findLibrarySequenceForFlow(
  librarySequences: readonly PlaybooksSequenceSummary[],
  flowId: string,
): PlaybooksSequenceSummary | undefined {
  // [R18] Plan 2026-09-28-007 U8 (Codex b2-3) — a Library sequence an admin
  // archived can no longer be chosen, so it is never offered for reuse.
  return librarySequences.find((sequence) => sequence.flowId === flowId && !isArchivedInLibrary(sequence));
}

/**
 * Round-3-QA-fixes plan, Codex adversarial-review finding F4 — thrown by
 * {@link createSequenceFromGongFlow} for any failure AFTER `createSequence`
 * has already committed (the Gong steps fetch, the bind, or the save). A
 * real, empty (unbound or bound-but-empty) sequence identity now sits in the
 * library — `sequenceId` is carried so a caller can invalidate/expand/point
 * at it, and this class exists specifically so a caller can tell "something
 * was created, but its steps didn't finish loading" apart from "nothing was
 * created at all" (a `createSequence` failure, which propagates as an
 * ordinary, un-wrapped error — see this function's own doc). Before this
 * fix, `PlaybooksConfigPage.tsx#handleCreateSave` caught EVERY rejection
 * from this function identically as "created but couldn't load steps",
 * which was actively wrong for a pre-create failure: it closed the create
 * form and told the rep something was created when nothing was.
 */
export class GongSequencePartialImportError extends Error {
  readonly sequenceId: string;

  constructor(sequenceId: string, cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'GongSequencePartialImportError';
    this.sequenceId = sequenceId;
    // `cause` is folded into `message` above rather than set as `this.cause`:
    // `web/app-home/tsconfig.json` targets lib ES2020, which predates
    // `Error.cause`, and `typecheck:web` compiles this file.
  }
}

/**
 * Round-3-QA-fixes plan, Unit C2 / decision 5 — the standalone "New Sequence"
 * form's Gong-creation path, factored out of `resolveGongFlowPick`'s case-1
 * created branch so both callers (the config page's create form and the
 * drawer's Gong tab) share one implementation. `name` is the caller's OWN
 * value (e.g. the create form's editable, flow-prefilled name field), never
 * `flow.name` directly — a brand-new identity has zero places mapped to it
 * yet, so no `confirmedMappingCount` is passed to `importGongFlowSteps`
 * (the server's default of `1` is already correct for a just-created row).
 * `confirmedUnsentPlayCount` is omitted for the same reason: a sequence that
 * did not exist a moment ago is on no playbook's ladder, so its real unsent-
 * play count is `0`, and the server's recheck only refuses when the ACTUAL
 * count exceeds what was acknowledged (`0 > 1` is false) — the default is
 * safe here, unlike on the already-mapped import/rebind paths.
 *
 * Failure ordering is create → fetch steps → bind → save, matching
 * `importGongFlowSteps`'s own doc:
 *  - `createSequence` fails: nothing exists at all — this rejection
 *    propagates UNTOUCHED (never wrapped), so a caller can distinguish it
 *    from every failure below by a plain `instanceof
 *    GongSequencePartialImportError` check (Codex finding F4).
 *  - The Gong steps fetch fails: a real, empty, UNBOUND sequence now sits in
 *    the library (no flow, no steps) — recoverable by picking a flow for it
 *    later, same as any other freshly-created sequence.
 *  - The bind (`setSequenceFlow`) fails: same as above — still unbound, still
 *    empty, still recoverable the same way.
 *  - The save (`saveSequenceSteps`) fails: the sequence is now BOUND to the
 *    flow but its head is still empty — recoverable in-play via
 *    `handleLoadStepsFromGong`'s "Load steps from Gong" affordance, which
 *    renders for an empty head (same recovery path `importGongFlowSteps`'s
 *    own doc describes for this partial-failure shape).
 * These three post-create failure shapes are indistinguishable from each
 * other on purpose here — a caller's "created but couldn't load steps" toast
 * is accurate for all three, and `importGongFlowSteps`'s own doc already
 * covers the finer distinction for anyone debugging the recovery path.
 */
export async function createSequenceFromGongFlow(
  apiFetch: ApiFetch,
  params: {
    flow: GongFlowSummary;
    name: string;
    /**
     * Playbooks QA round 4, U9 (SN1) — the sender to lock the new sequence to,
     * collected by the same `SequenceCreateFields` form the Gong doorway
     * renders. Optional: the import flow legitimately creates sequences before
     * a sender is chosen ("Not set until edited", Decision 10).
     */
    sender?: string | null;
    /**
     * Plan 2026-09-25-002, U6 (Decision 13) — the play that owns the new
     * sequence, when created inside `/play/:id`. Omitted in the playbook
     * doorway, where the create's wire body is unchanged.
     */
    owningPlayId?: string;
  },
): Promise<{ sequenceId: string; steps: SequenceSteps; save: PlaybooksSaveSequenceStepsResponse }> {
  const { flow, name, sender, owningPlayId } = params;
  const created =
    owningPlayId === undefined
      ? await createSequence(apiFetch, name, 'gong_engage', sender ?? null)
      : await createSequence(apiFetch, name, 'gong_engage', sender ?? null, { owningPlayId });
  try {
    const { steps, save } = await importGongFlowSteps(apiFetch, {
      sequenceId: created.id,
      flowId: flow.flow_id,
      headVersionId: created.versionId,
    });
    return { sequenceId: created.id, steps, save };
  } catch (err) {
    throw new GongSequencePartialImportError(created.id, err);
  }
}

/**
 * The Gong tab's single dispatch point (plan D2): given the flow the rep just
 * clicked, the rung's currently-mapped sequence (if any), and the library's
 * sequences (for the case-1 reuse check), decides which of the three cases
 * applies and does every write that doesn't require a confirm.
 *
 * `mappedSequence` is the CALLER's job to resolve (looking up
 * `currentMapping.sequenceId` in its own `sequences` prop) rather than a
 * second parameter this function derives itself — the caller already has
 * both in hand from render, and passing the resolved summary directly avoids
 * this function needing its own opinion about what "mapped to a non-Gong
 * sequence" means structurally.
 *
 * Case boundary logic:
 *  - `mappedSequence` absent or not `gong_engage`: case 1 (create-or-reuse).
 *  - `mappedSequence.flowId === flow.flow_id`: no-op (same flow re-picked).
 *  - `mappedSequence.flowId === null` (a TRUE first bind): fetch the mapped
 *    sequence's real head. An EMPTY head has nothing to lose, so it imports
 *    immediately, no confirm (case 2) — EXCEPT inside a play when the
 *    sequence is shared (`!gongRebindIsSoleUse(detail)`, plan 2026-09-28-006
 *    U6, rulebook R5/R17): that is a shared edit, so it returns
 *    `'confirm-rebind'` with `headSteps: []` and the drawer's Save menu
 *    decides. A NON-EMPTY head (the rare edge case —
 *    a first bind that somehow already has non-empty placeholder steps) needs
 *    the confirm-rebind round trip, since there is real saved copy to protect
 *    even though there is no "old flow" to name in the dialog.
 *  - Otherwise (`mappedSequence.flowId` is a DIFFERENT non-null flow — a
 *    genuine rebind): ALWAYS routes to confirm-rebind, regardless of whether
 *    the head is empty. Live-QA round 2 Batch 6 fix — a rebind is
 *    identity-level: it rewrites the sequence's SHARED `flow_id`, which every
 *    OTHER playbook/rung mapping this sequence also sees, and it suppresses
 *    the `activeMappingCount > 1` warning the confirm dialog shows. "Nothing
 *    to lose in copy" (an empty head) is not the same as "nothing to lose,
 *    period" when other playbooks are silently repointed too — only a TRUE
 *    first bind (nothing else could have been depending on the old
 *    `flow_id`, because there wasn't one) is allowed to skip the confirm on
 *    an empty head. `resolveGongFlowPick`'s caller
 *    (`SequenceMappingDrawer.tsx`'s `openRebindConfirm`) softens the dialog
 *    copy for this empty-head-but-shared case instead of skipping it.
 */
export async function resolveGongFlowPick(
  apiFetch: ApiFetch,
  params: {
    flow: GongFlowSummary;
    mappedSequence: PlaybooksSequenceSummary | undefined;
    librarySequences: readonly PlaybooksSequenceSummary[];
    /** Plan 2026-09-25-002, U6 (Decision 13) — a case-1 create inside a play is the play's own. */
    owningPlayId?: string;
    /**
     * Plan 2026-09-29-001 U1 (R3/R5/R14) — the group (rung) this pick is
     * for, given whenever `owningPlayId` is (never independently). Forwarded
     * to the one no-dialog write below (`importGongFlowSteps`'s `group`) —
     * that write is sole-use by construction (`gongRebindIsSoleUse`), so the
     * group set it was checked against is exactly this one group.
     */
    rungId?: string;
    /**
     * Plan 2026-09-27-007 U3 — inside a play, resolves the play's own copy of
     * `sequenceId` (`ensure-play-owned.ts#ensurePlayOwned`; a no-op returning
     * the same id when the play already owns it). Called ONLY in the
     * first-bind import branch — the one outcome that writes with no confirm,
     * so the rep's click already IS the choice. Never called for
     * `confirm-rebind`: ownership there is decided by the caller once the rep
     * answers the dialog, so a Cancel leaves no copy behind. `undefined` in
     * the playbook doorway.
     */
    ensureOwned?: (
      sequenceId: string,
      /**
       * [R14] Plan 2026-09-28-007 U8 (Codex c2-3) — the head this pick
       * decided on (the empty head it just read), so the fork is refused
       * rather than silently overwriting steps someone else just added.
       */
      expectedSourceVersionId: string,
    ) => Promise<{ ownedId: string; headVersionId: string }>;
  },
): Promise<GongFlowPickOutcome> {
  const { flow, mappedSequence, librarySequences, owningPlayId, rungId, ensureOwned } = params;

  if (!mappedSequence || mappedSequence.channel !== 'gong_engage') {
    const reused = findLibrarySequenceForFlow(librarySequences, flow.flow_id);
    if (reused) return { kind: 'selected', sequenceId: reused.id, created: false };
    const { sequenceId } = await createSequenceFromGongFlow(apiFetch, {
      flow,
      name: flow.name,
      ...(owningPlayId === undefined ? {} : { owningPlayId }),
    });
    return { kind: 'selected', sequenceId, created: true };
  }

  if (mappedSequence.flowId === flow.flow_id) {
    return { kind: 'unchanged', sequenceId: mappedSequence.id };
  }

  const detail = await getSequence(apiFetch, mappedSequence.id);
  const headSteps = detail.head?.steps ?? [];
  const headVersionId = detail.head?.versionId;
  if (!headVersionId) {
    throw new Error('This sequence hasn\'t finished saving yet. Reload and try again.');
  }

  // Codex fix 2 (round 2 of Batch 6 hardening): every decision from here on
  // uses `detail.sequence.flowId` — the FRESH value this `getSequence` call
  // just fetched — never the caller's possibly-stale `mappedSequence.flowId`
  // prop. The cheap pre-fetch fast-path above is fine as an optimization for
  // the common case, but it can be wrong if another tab/session rebound this
  // sequence between the library list's last fetch and this click, and this
  // is the one authoritative point where fresh data is in hand.
  const currentFlowId = detail.sequence.flowId;

  if (currentFlowId === flow.flow_id) {
    return { kind: 'unchanged', sequenceId: mappedSequence.id };
  }

  // Fix 2 (Live-QA round 2, Batch 6): only a TRUE first bind
  // (`currentFlowId === null`) may skip the confirm on an empty
  // head. A rebind onto a DIFFERENT already-bound flow is identity-level —
  // it rewrites the shared `flow_id` other playbooks/rungs see — so it
  // always routes to confirm-rebind below, even when there is no copy to
  // lose. See this function's doc comment.
  // Plan 2026-09-28-006 U6 (rulebook R5, R17) — inside a play, a first bind
  // onto a SHARED empty sequence is still a shared edit: loading the flow
  // changes what every other group using this sequence sends. It returns
  // `'confirm-rebind'` (with no steps to lose, `headSteps: []`) so the
  // drawer's Save menu decides — nothing is imported or copied here. Only a
  // sequence this group alone uses (`gongRebindIsSoleUse`) imports silently.
  // The playbook doorway (`owningPlayId === undefined`) is unchanged.
  const firstBindIsShared = owningPlayId !== undefined && !gongRebindIsSoleUse(detail);
  if (currentFlowId === null && headSteps.length === 0 && !firstBindIsShared) {
    // Plan 2026-09-27-006 U7a, Key Technical Decision 12 — a play's first
    // bind on an unbound, empty-headed sequence only this group uses follows
    // the "no dialog" rule. Plan 2026-09-27-007 U3 — ownership is
    // resolved HERE, right before the write, never earlier: this is the only
    // branch that writes without a confirm, so it is the only place a copy
    // may be made before the rep has seen a dialog. A fresh copy is used in
    // exactly this one place (counts 1/1); an id the play already owned keeps
    // the counts this `getSequence` resolved for it.
    const owned = ensureOwned
      ? await ensureOwned(mappedSequence.id, headVersionId)
      : { ownedId: mappedSequence.id, headVersionId };
    const isFreshCopy = owned.ownedId !== mappedSequence.id;
    // Plan 2026-09-29-001 U1 (R3/R5/R14) — an in-play write always names its
    // group; `owningPlayId` and `rungId` travel together (the caller passes
    // both or neither).
    const importOpts: SequenceSettingsWriteOptions =
      owningPlayId === undefined ? { playId: undefined } : { playId: owningPlayId, group: { rungId: rungId!, confirmedRungIds: [rungId!] } };
    const { steps, save } = await importGongFlowSteps(apiFetch, {
      sequenceId: owned.ownedId,
      flowId: flow.flow_id,
      headVersionId: owned.headVersionId,
      confirmedMappingCount: isFreshCopy ? 1 : detail.activeMappingCount,
      confirmedUnsentPlayCount: isFreshCopy ? 1 : detail.activeUnsentPlayCount,
      ...importOpts,
    });
    return {
      kind: 'imported',
      sequenceId: owned.ownedId,
      stepCount: steps.length,
      flowName: flow.name,
      save,
    };
  }

  // Plan 2026-09-27-007 U3 — nothing is written or copied for this outcome.
  // The caller decides ownership at confirm time, from the rep's choice
  // (owned → load onto the copy; sole-use → copy then load; shared →
  // "Update N uses" or "Save as new").
  const stepsResponse = await fetchGongFlowSteps(apiFetch, flow.flow_id, { force: true });
  const mappedSteps = gongStepsToSequenceSteps(stepsResponse.steps);
  return {
    kind: 'confirm-rebind',
    sequenceId: mappedSequence.id,
    fromFlowId: currentFlowId,
    headSteps,
    headVersionId,
    mappedSteps,
    activeMappingCount: detail.activeMappingCount,
    activeUnsentPlayCount: detail.activeUnsentPlayCount,
  };
}

/**
 * The confirmed WRITE half of case 3 (plan D4): `resolveGongFlowPick` already
 * fetched and mapped the new flow's steps for the dialog, so this function
 * skips straight to bind → save — no second fetch, keeping the copy the rep
 * reviewed in the dialog byte-for-byte identical to what gets saved (a
 * second live fetch here could race a Gong-side edit between the dialog
 * opening and the rep clicking confirm).
 *
 * Fix 1 (Live-QA round 2, Batch 6) — bind-then-save is two sequential,
 * non-transactional HTTP writes. If the bind (`setSequenceFlow`) succeeds but
 * the save (`saveSequenceSteps`, CAS'd on `headVersionId`) then fails (most
 * plausibly a real `version_conflict`), the sequence is left bound to the NEW
 * flow while its saved head still shows the OLD flow's (or a manually-typed)
 * copy — silently corrupted, and Batch 4's "Load steps from Gong" retry
 * affordance only renders for an EMPTY head, so this specific state has no
 * recovery UI. On a save failure this function attempts a compensating
 * rollback: rebind back to `fromFlowId` (legal and idempotent-safe per
 * `setSequenceFlowId`'s doc, since it is a concrete, already-observed prior
 * value) before re-throwing the original save error.
 *
 * `fromFlowId === null` (a genuine first-bind gone wrong — there was no prior
 * flow to restore) is NOT symmetric: `setSequenceFlow`/`setSequenceFlowId`
 * have no unset path (both are typed `flowId: string`, never `null` — v1's
 * free-text-override "unset" concept was dropped for good, see
 * `setSequenceFlowId`'s own doc), so there is no legal rollback call to make.
 * That case skips straight to the manual-reconciliation error below instead
 * of attempting an invalid `setSequenceFlow(apiFetch, sequenceId, null)` call.
 *
 * If the rollback attempt (when one is legal) ALSO fails, the thrown error is
 * a distinct, explicitly-worded "this needs manual reconciliation" message —
 * NOT the normal retryable-conflict wording — since a reload-and-retry cannot
 * fix a sequence left bound to a flow whose steps were never saved for it.
 *
 * Codex fix 1 (round 2 of Batch 6 hardening) — the rollback's `setSequenceFlow`
 * call is itself a compare-and-set with NO WAY to receive "only proceed if the
 * current value is still `flowId` (the one I'm undoing)" from this caller;
 * `setSequenceFlowId` always CASes against whatever it freshly observes at
 * call time. An unconditional rollback can therefore clobber a legitimate
 * intervening write: if some OTHER concurrent rebind (a different rep/tab)
 * successfully bound a DIFFERENT flow and saved its steps in the gap between
 * this function's failed save and its rollback attempt, blindly rolling back
 * to `fromFlowId` would silently overwrite that other, successful write —
 * leaving the identity claiming `fromFlowId` while its saved head content is
 * really the other write's. To close most of that window, this function
 * re-fetches the sequence immediately before rolling back and only attempts
 * the rollback if the sequence's CURRENT `flowId` still equals `flowId` (the
 * value being undone) — i.e. nothing else has superseded our bind yet. If it
 * no longer matches, skipping the rollback and going straight to a manual-
 * reconciliation error is strictly safer than reverting someone else's
 * already-successful write; this is not a perfect atomic CAS (there is still
 * a small window between this re-fetch and the rollback write itself), but it
 * closes the vast majority of the practical race window compared to not
 * checking at all.
 *
 * Round-3-QA-fixes plan, Unit C2 / decision 10 — `activeMappingCount` is
 * required (every caller has it in hand from the confirm dialog's own
 * `outcome`, which is what its copy was built from) and is forwarded on the
 * save below as `saveSequenceSteps`'s shared-save acknowledgment — omitting
 * it is exactly root cause (a): the server defaults to `1` and a rebind on a
 * sequence used in 2+ places 409s deterministically. The rollback path is
 * unaffected by this — it never calls `saveSequenceSteps` again.
 *
 * Round-4 QA codex-review fix (wave 1, finding 1) — `activeUnsentPlayCount`
 * is required for the identical reason on the second axis: the save endpoint
 * rechecks `countUnsentPlaysForSequence` against its OWN default of `1`, so a
 * rebind of a sequence carrying 2+ draft plays 409'd every time regardless of
 * how many rungs mapped it. Both counts come from the `confirm-rebind`
 * outcome, i.e. from the single `getSequence` this rebind's dialog was built
 * from — never re-derived here, where a fresh read could disagree with the
 * copy the rep just approved.
 */
export async function commitGongFlowRebind(
  apiFetch: ApiFetch,
  params: {
    sequenceId: string;
    flowId: string;
    fromFlowId: string | null;
    headVersionId: string;
    mappedSteps: SequenceSteps;
    activeMappingCount: number;
    activeUnsentPlayCount: number;
    /**
     * Plan 2026-09-27-007 U3 — the play whose OWN copy `sequenceId` is.
     * Forwarded as `?playId=` to the bind, the save and the rollback bind.
     * TIGHTENED to a union by plan 2026-09-29-001 U1 (R3/R5/R14): an in-play
     * rebind now names its group too.
     */
  } & SequenceSettingsWriteOptions,
): Promise<PlaybooksSaveSequenceStepsResponse> {
  const {
    sequenceId,
    flowId,
    fromFlowId,
    headVersionId,
    mappedSteps,
    activeMappingCount,
    activeUnsentPlayCount,
  } = params;
  // Plan 2026-09-27-007 U3 — two shapes, chosen by the caller from the rep's
  // answer to the confirm:
  //  - WITHOUT `playId`: a Library (template) write — the doorway, or the
  //    in-play "Update N uses" choice. Every rung/playbook mapping this
  //    sequence sees the new flow, which is what that button says. The
  //    doorway / "Update N uses" call shape is unchanged (no opts argument).
  //  - WITH `playId`: a write to the play's own copy — the in-play owned,
  //    sole-use, or "Save as new" choice. The copy is used only by this play
  //    by construction, so the Library and other plays are untouched.
  const bind = (toFlowId: string) =>
    params.playId === undefined
      ? setSequenceFlow(apiFetch, sequenceId, toFlowId)
      : setSequenceFlow(apiFetch, sequenceId, toFlowId, { playId: params.playId, group: params.group });
  await bind(flowId);
  try {
    return await (params.playId === undefined
      ? saveSequenceSteps(apiFetch, sequenceId, mappedSteps, headVersionId, activeMappingCount, undefined, activeUnsentPlayCount)
      : saveSequenceSteps(
          apiFetch,
          sequenceId,
          mappedSteps,
          headVersionId,
          activeMappingCount,
          undefined,
          activeUnsentPlayCount,
          { playId: params.playId, group: params.group },
        ));
  } catch (saveError) {
    const saveErrorMessage = saveError instanceof Error ? saveError.message : String(saveError);
    if (fromFlowId === null) {
      // No legal rollback target — this is a genuine first bind gone wrong.
      // Surfacing the normal retryable-conflict wording here would tell the
      // rep to "reload and try again", which cannot fix a sequence left
      // bound to a flow whose steps were never actually saved for it.
      throw new Error(
        `This Sequence is now bound to a new Gong flow, but saving its steps failed and there is no ` +
          `previous flow to automatically restore (this was a first bind) — this needs manual ` +
          `reconciliation, not a reload-and-retry. Original error: ${saveErrorMessage}`,
      );
    }
    // Re-fetch to see whether our bind is still the current state before
    // rolling it back — see this function's doc comment (Codex fix 1).
    let currentFlowId: string | null;
    try {
      const current = await getSequence(apiFetch, sequenceId);
      currentFlowId = current.sequence.flowId;
    } catch (refetchError) {
      const refetchErrorMessage =
        refetchError instanceof Error ? refetchError.message : String(refetchError);
      throw new Error(
        `This Sequence is now bound to a new Gong flow, but saving its steps failed AND checking ` +
          `whether it is still safe to roll back also failed — this needs manual reconciliation, not a ` +
          `reload-and-retry. Save error: ${saveErrorMessage}. Recheck error: ${refetchErrorMessage}`,
      );
    }

    if (currentFlowId !== flowId) {
      // Someone else's legitimate rebind has already superseded ours — a
      // blind rollback here would clobber their successful write. Do not
      // roll back; this state needs a human, not a reload-and-retry.
      throw new Error(
        `This Sequence is now bound to a new Gong flow, but saving its steps failed — and before this ` +
          `could automatically roll back to the previous flow, another update changed this Sequence's ` +
          `binding, so rolling back would have overwritten that other change — this needs manual ` +
          `reconciliation, not a reload-and-retry. Save error: ${saveErrorMessage}`,
      );
    }

    try {
      await bind(fromFlowId);
    } catch (rollbackError) {
      const rollbackErrorMessage =
        rollbackError instanceof Error ? rollbackError.message : String(rollbackError);
      throw new Error(
        `This Sequence is now bound to a new Gong flow, but saving its steps failed AND the automatic ` +
          `rollback to its previous flow also failed — this needs manual reconciliation, not a ` +
          `reload-and-retry. Save error: ${saveErrorMessage}. Rollback error: ${rollbackErrorMessage}`,
      );
    }
    throw saveError;
  }
}

// ── Browse-list folder grouping (pure) ──────────────────────────────────────

/**
 * PR B follow-up U9 (R9) — groups an already-sorted flow list into labeled
 * folder sections for the browse list. Single pass, ORDER-PRESERVING: each
 * group's rows keep their relative order from `flows`, and grouping never
 * moves a row relative to the caller's own sort. Callers must pass
 * `filterAndSortGongFlows`'s OUTPUT here, already filtered/sorted — this
 * helper only regroups, it never re-ranks.
 *
 * ── CODEX HARDENING FIX — CONTIGUOUS-RUN grouping, not "merge into this
 * folder's first-seen group": a new group starts only when the current
 * flow's folder differs from the group most recently pushed, checked via
 * `groups[groups.length - 1]`. The previous implementation merged every
 * occurrence of a folder name into whichever group first saw it (an
 * `indexByFolder` map), which is wrong whenever the incoming pinned/relevance
 * sort interleaves the same folder with a different one: e.g.
 * `[pinned A (folder X), pinned B (folder Y), unpinned A2 (folder X)]` would
 * merge `A2` backward into folder X's group (created at A's position),
 * pushing pinned B's group AFTER unpinned A2's row even though the caller's
 * sort correctly ranked B ahead of A2. Grouping-by-folder-name-globally fights
 * the sort in exactly that non-contiguous case. Contiguous-run grouping fully
 * preserves the incoming order instead — a folder legitimately gets more than
 * one heading if the sort interleaves it away from its own earlier run, which
 * is correct given the sort (not the folder name) is the source of truth for
 * ordering.
 *
 * `flow.folder_name ?? 'Unfiled'` is the same fallback label the per-row
 * folder `Badge` already used before grouping existed (`GongFlowPicker.tsx`).
 * The Suggested rail deliberately does NOT call this — it stays flat (plan
 * Decision 8): a small curated top-3 gains nothing from folder sections.
 */
export function groupGongFlowsByFolder(
  flows: GongFlowSummary[],
): { folder: string; flows: GongFlowSummary[] }[] {
  const groups: { folder: string; flows: GongFlowSummary[] }[] = [];
  for (const flow of flows) {
    const folder = flow.folder_name ?? 'Unfiled';
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.folder === folder) {
      lastGroup.flows.push(flow);
    } else {
      groups.push({ folder, flows: [flow] });
    }
  }
  return groups;
}
